import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip } from '@/shared/ui/states'
import { Pill, SubHeader, fmtDate, label } from '@/features/dossier/components/primitives'
import {
  askAdvice, listAdvice, listOperations, moveStage,
  type Advice, type Operation, type Target,
} from './api'

import { STAGE_ORDER, STAGE_GATE, stageLabel } from './stages'

const STAGES = [...STAGE_ORDER]

/**
 * The five playbooks this door runs, each with what it is FOR. Mirrors
 * TargetAdviceService.PLAYBOOKS — all five are available at every stage.
 */
const PLAYBOOKS: [string, string, string][] = [
  ['OPPONENT_DOSSIER', 'Opponent dossier & assessment',
    'The 360 read: standing, history, documented exposure, network, and what we do not know'],
  ['VULNERABILITY_ASSESSMENT', 'Vulnerability & attack-surface assessment',
    'Rank the exposures by severity, public exposure and defensibility, with the rebuttal to each'],
  ['COUNTER_NARRATIVE', 'Counter-narrative & rapid response',
    'What they will say, and what we say back'],
  ['DEFENSIVE_POSTURE', 'What they would attack us with',
    'Run against OUR OWN candidate — pre-build the defence before the attack lands'],
  ['DOSSIER_GAP_SCAN', 'Dossier gap scan & collection plan',
    'What we do not know about this subject, and what would close it'],
]

/** Which one the stage suggests. A DEFAULT, never a restriction. */
const SUGGESTED: Record<string, string> = {
  RECON: 'OPPONENT_DOSSIER',
  WEAPONIZE: 'VULNERABILITY_ASSESSMENT',
  DEPLOY: 'COUNTER_NARRATIVE',
  SUSTAIN: 'COUNTER_NARRATIVE',
  NEUTRALIZE: 'DOSSIER_GAP_SCAN',
}

/**
 * What each gate will check, said BEFORE the click rather than in a 422 after it.
 *
 * Shared with the board so the column head and the move button cannot drift apart, and
 * phrased as a requirement a campaign can meet rather than as the rule's own wording.
 */
const GATES = STAGE_GATE

/**
 * The kill chain: where this target is, what the next move needs, and the council.
 *
 * ## The gate is stated before the click, not after the refusal
 *
 * Every transition is earned — the service checks evidence and writes what it checked into
 * the transition row. That check used to be reachable only by making the move and reading the
 * 422. Naming the gate up front turns "computer says no" into a task list.
 *
 * ## A stage cannot be skipped, so only one button is offered
 *
 * Each gate checks the evidence the previous one produced. A chain with a hole in it proves
 * nothing, which is why this offers the NEXT stage and not a menu of five.
 *
 * ## A second campaign against the same target is an OPERATION, not a second target
 *
 * The target's stage is the campaign's POSTURE toward one person. An operation is an
 * EXECUTION — its own chain, its own plays, its own budget, its own outcome — and several can
 * run against one target over a cycle. So "we have new material and the execution is
 * completely different" is a new operation, not a rewind: the first operation's history stays
 * intact and the new one starts at Recon with its own gates.
 *
 * (A second TARGET on the same subject is not available and should not be: the unique index
 * on (election, subject) refuses it, and it would split one person's history in two.)
 *
 * Stepping the target's own stage back is a different, smaller thing — for when the same
 * campaign genuinely needs to re-enter a stage. Re-entering RE-RUNS that gate, so it is not a
 * way round the evidence.
 *
 * ## The council is ALWAYS available
 *
 * All five playbooks, at every stage, including after the target has been closed out — the
 * chain's position is not a reason to withhold a question. The stage only preselects the one
 * that usually fits. What is still refused is a free PROMPT: choosing among reviewed recipes
 * is not the same act as writing one.
 *
 * ## What the council can and cannot do
 *
 * It writes a brief. It cannot draft a play, clear one, or move a stage — plays still start
 * DRAFT and still need a review by somebody other than their author, and the gates check
 * evidence rather than advice. Every ask is recorded against the target and the stage it was
 * at, with the question as typed, the playbook, the model that answered and the person who
 * asked, so "what does our weaponize strategy look like, and who asked for it" is a query.
 */
export function KillChainPanel({ target, canOperate, canPlan, onMoved }: {
  target: Target
  /** `opposition:operate` — moving the chain. */
  canOperate: boolean
  /** `opposition:plan` — asking the council. */
  canPlan: boolean
  onMoved: () => void
}) {
  const [advice, setAdvice] = useState<Advice[]>([])
  const [operations, setOperations] = useState<Operation[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')
  const [asking, setAsking] = useState(false)
  const [question, setQuestion] = useState('')
  const [playbookCode, setPlaybookCode] = useState(
    SUGGESTED[target.killChainStageCode] ?? 'OPPONENT_DOSSIER')
  const [reopening, setReopening] = useState(false)
  const [reopenNote, setReopenNote] = useState('')

  const current = STAGES.indexOf(target.killChainStageCode)
  const next = current >= 0 && current < STAGES.length - 1 ? STAGES[current + 1] : null
  const previous = current > 0 ? STAGES[current - 1] : null
  const closedOut = target.killChainStageCode === 'NEUTRALIZE'

  const load = useCallback(async () => {
    try {
      const [asks, ops] = await Promise.all([
        listAdvice(target.id),
        listOperations(target.electionId),
      ])
      setAdvice(asks)
      // Several can run at once against one person, each at its own stage.
      setOperations(ops.filter((o) => o.targetId === target.id))
    } catch (err) {
      setError(explain(err))
    }
  }, [target.id, target.electionId])

  useEffect(() => { void load() }, [load])

  async function move(stageCode: string, why?: string) {
    setBusy(true)
    setError(null)
    try {
      await moveStage(target.id, stageCode, (why ?? note).trim() || undefined)
      setNote('')
      setReopenNote('')
      setReopening(false)
      onMoved()
    } catch (err) {
      // The 422 names the unmet gate and what would satisfy it. Shown verbatim — it is more
      // useful than anything this screen could paraphrase.
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  async function ask() {
    setBusy(true)
    setError(null)
    try {
      await askAdvice(target.id, {
        playbookCode,
        question: question.trim() || undefined,
      })
      setQuestion('')
      setAsking(false)
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  const suggested = SUGGESTED[target.killChainStageCode]
  const chosen = PLAYBOOKS.find(([code]) => code === playbookCode)

  return (
    <div className="space-y-2 p-2">
      <SubHeader>Kill chain</SubHeader>

      {error && <ErrorStrip message={error} />}

      <div className="flex flex-wrap items-center gap-1">
        {STAGES.map((stage, i) => (
          <span key={stage} className="flex items-center gap-1">
            <Pill className={i <= current
              ? 'border-primary/40 bg-primary/10 text-primary'
              : 'border-border text-muted-foreground'}>
              {stageLabel(stage)}
            </Pill>
            {i < STAGES.length - 1 && <span className="text-muted-foreground">›</span>}
          </span>
        ))}
      </div>

      {next === 'NEUTRALIZE' ? (
        // NEUTRALIZE has no button on purpose. A target reaches it by having the operation
        // run against it closed with an outcome — the gate refuses a bare move and says so,
        // and offering a button that exists to be rejected teaches nothing.
        <div className="space-y-1 rounded border border-border p-2">
          <p className="text-[12px] font-medium">Next: {label(next)} — through the operation</p>
          <p className="text-[11px] text-muted-foreground">
            There is no button here. A file is closed out when the operation run
            against it is <b>closed with an outcome</b>: the close records what happened and
            moves the target, writing that outcome into the stage history. Open or find it
            under{' '}
            <Link to="/opposition/operations" className="text-primary underline">
              Active Operations
            </Link>
            {' '}and close it out.
          </p>
        </div>
      ) : next ? (
        <div className="space-y-1.5 rounded border border-border p-2">
          <p className="text-[12px] font-medium">Next: {label(next)}</p>
          <p className="text-[11px] text-muted-foreground">
            The gate checks {GATES[next]}. What it checked is written into the transition, so
            the move can be defended later rather than merely dated.
          </p>
          {canOperate ? (
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-1 flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Note (optional, kept on the transition)
                </span>
                <input value={note} onChange={(e) => setNote(e.target.value)}
                       placeholder="Why now?"
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
              <button type="button" disabled={busy} onClick={() => void move(next)}
                      className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                                 text-primary-foreground disabled:opacity-50">
                {busy ? 'Checking the gate…' : `Move to ${label(next)}`}
              </button>
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              Moving the chain needs <b>opposition:operate</b>.
            </p>
          )}
        </div>
      ) : null}

      {/* Stepping BACK is always allowed by the chain — it claims nothing, and re-entering a
          stage RE-RUNS that stage's gate, so the evidence has to still stand. This is the
          answer to "a new exposure turned up after we closed": the chain is a posture, not a
          one-way ratchet, and the alternative — a second target on the same subject — is
          refused by the unique index and would split the history in two. */}
      {/* THE ANSWER to "we have new material and a different execution". A new operation is
          the campaign's second run at this person: its own chain, its own plays, its own
          budget, its own outcome, with the first one's history left intact. */}
      <div className="space-y-1 rounded border border-border p-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[12px] font-medium">Run a new campaign against this target</p>
          <Link to="/opposition/operations"
                className="rounded border border-border px-2 py-0.5 text-[11px]">
            Open an operation
          </Link>
        </div>
        <p className="text-[11px] text-muted-foreground">
          New material, a different execution, a different budget? That is a <b>new
          operation</b>, not a rewind. An operation walks its own five stages with its own
          gates and closes with its own outcome, and <b>several can run at the same time</b>
          against one target — the earlier one's record stays exactly as it was.
          {closedOut && ' This target is closed out, which does not stop that: the operation '
            + 'has its own chain. Its posture below stays closed out until you step it '
            + 'back, so the matrix keeps telling the truth about what has been concluded.'}
        </p>

        {operations.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {operations.map((op) => (
              <li key={op.id} className="flex flex-wrap items-center gap-2 text-[11px]">
                <span className="font-mono text-[10px] text-muted-foreground">{op.ref}</span>
                <span className="flex-1">{op.name}</span>
                <Pill className="border-border">{stageLabel(op.stageCode)}</Pill>
                {op.outcomeCode ? (
                  <Pill className={op.outcomeCode === 'BACKFIRED'
                    ? 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400'
                    : 'border-border'}>
                    {label(op.outcomeCode)}
                  </Pill>
                ) : (
                  <span className="text-[10px] text-muted-foreground">running</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {previous && (
        <div className="space-y-1.5 rounded border border-border p-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-[12px] font-medium">
              {closedOut ? 'Or put this target back in play'
                : `Step back to ${label(previous)}`}
            </p>
            {canOperate && (
              <button type="button" onClick={() => setReopening((v) => !v)}
                      className="rounded border border-border px-2 py-0.5 text-[11px]">
                {reopening ? 'Cancel'
                  : closedOut ? 'Back to building the case' : `Back to ${stageLabel(previous)}`}
              </button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            {closedOut
              ? 'Only if the SAME campaign is resuming — this changes the posture on the '
                + 'threat matrix and the board. Going back to building the case re-runs that gate '
                + 'against the evidence as it stands now, and every earlier transition '
                + 'survives. If the execution is genuinely different, open an operation '
                + 'instead and leave this where it is.'
              : 'Re-entering a stage re-runs its gate, so this is not a way round the '
                + 'evidence. The earlier transitions stay on the record.'}
          </p>
          {reopening && canOperate && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-1 flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Why (required — it goes on the transition)
                </span>
                <input value={reopenNote} onChange={(e) => setReopenNote(e.target.value)}
                       placeholder={closedOut
                         ? 'New surface recorded: undeclared shareholding'
                         : 'What changed?'}
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
              <button type="button" disabled={busy || !reopenNote.trim()}
                      onClick={() => void move(closedOut ? 'WEAPONIZE' : previous, reopenNote)}
                      className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                                 text-primary-foreground disabled:opacity-50">
                {busy ? 'Checking the gate…'
                  : closedOut ? 'Re-open to build the case' : `Move back to ${stageLabel(previous)}`}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="space-y-1.5 rounded border border-border p-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 text-[12px] font-medium">
            <Sparkles className="size-3.5 text-primary" />
            Ask the council
          </p>
          {canPlan && (
            <button type="button" onClick={() => setAsking((v) => !v)}
                    className="rounded border border-border px-2 py-0.5 text-[11px]">
              {asking ? 'Close' : 'Ask'}
            </button>
          )}
        </div>

        <p className="text-[11px] text-muted-foreground">
          {/* Always. The chain's position is not a reason to withhold a question. */}
          Any of the five playbooks, at any stage — including after this target has been closed
          out. Each runs over this subject's dossier and writes a brief citing the Admiralty
          grade of every row it used; none of them can draft a play, clear one, or move the
          chain. Every ask is recorded below against your name.
        </p>

        {asking && (
          <div className="space-y-1.5">
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Which question
              </span>
              <select value={playbookCode} onChange={(e) => setPlaybookCode(e.target.value)}
                      className="h-7 rounded border border-input bg-background px-2
                                 text-[12px]">
                {PLAYBOOKS.map(([code, title]) => (
                  <option key={code} value={code}>
                    {title}{code === suggested ? ' — suggested here' : ''}
                  </option>
                ))}
              </select>
              {chosen && (
                <span className="text-[10px] text-muted-foreground">{chosen[2]}</span>
              )}
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Your question (optional)
              </span>
              <textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows={2}
                        placeholder="Anything specific you want the brief to answer."
                        className="rounded border border-input bg-background px-2 py-1
                                   text-[12px]" />
            </label>
            <button type="button" disabled={busy} onClick={() => void ask()}
                    className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                               text-primary-foreground disabled:opacity-50">
              {busy ? 'Queueing…' : 'Ask the council'}
            </button>
          </div>
        )}

        {advice.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">Nothing asked yet.</p>
        ) : (
          <ul className="space-y-1">
            {advice.map((a) => (
              <li key={a.id} className="rounded border border-border p-1.5">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-[11px] font-medium">
                    {stageLabel(a.stageCode)} · {a.playbookCode}
                  </span>
                  <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                    <Pill className={a.runStatusCode === 'COMPLETED'
                      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                      : a.runStatusCode === 'FAILED'
                      ? 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400'
                      : 'border-border'}>
                      {label(a.runStatusCode)}
                    </Pill>
                    {fmtDate(a.requestedAt)}
                  </span>
                </div>
                {a.question && (
                  <p className="text-[10px] italic text-muted-foreground">“{a.question}”</p>
                )}
                {a.headline && <p className="mt-0.5 text-[11px]">{a.headline}</p>}
                {a.executiveSummary && (
                  <p className="mt-0.5 whitespace-pre-wrap text-[11px] text-muted-foreground">
                    {a.executiveSummary}
                  </p>
                )}
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[10px]
                                text-muted-foreground">
                  {/* Which model wrote it, on the row. "The AI said" is not an attribution. */}
                  {a.generatedByModel && <span>{a.generatedByModel}</span>}
                  {a.confidenceCode && <span>confidence {label(a.confidenceCode)}</span>}
                  <Link to={`/advisory/runs/${a.advisorRunId}`} className="text-primary underline">
                    the full brief, its inputs and its citations →
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
